import React, { useState, useEffect, useRef } from 'react';
import './portfolio.css';

const projects = [
  {
    id: 1,
    title: 'Crypto Dashboard',
    category: 'crypto',
    image: 'https://images.unsplash.com/photo-1622630998477-20aa696ecb05?w=600&h=400&fit=crop',
    description: 'Real-time cryptocurrency tracking and analytics platform',
    icon: '₿',
    link: 'https://batmaninu.onrender.com/',
    gradient: 'from-amber-500 to-orange-500'
  },
  {
    id: 2,
    title: 'Guitar Store',
    category: 'ecommerce',
    image: 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=600&h=400&fit=crop',
    description: 'Full-featured e-commerce platform for musical instruments',
    icon: '🎸',
    link: 'https://guitargroove.onrender.com/',
    gradient: 'from-emerald-500 to-teal-500'
  },
  {
    id: 3,
    title: 'Portfolio Pro',
    category: 'portfolio',
    image: 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=600&h=400&fit=crop',
    description: 'Modern developer portfolio with dynamic content',
    icon: '🎨',
    link: 'https://personalportfolio1.onrender.com/',
    gradient: 'from-purple-500 to-pink-500'
  },
  {
    id: 4,
    title: 'Blockchain Explorer',
    category: 'crypto',
    image: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=600&h=400&fit=crop',
    description: 'Blockchain transaction explorer and analytics tool',
    icon: '⛓️',
    link: 'https://batmaninu.onrender.com/',
    gradient: 'from-blue-500 to-cyan-500'
  },
  {
    id: 5,
    title: 'Fashion Store',
    category: 'ecommerce',
    image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&h=400&fit=crop',
    description: 'Premium fashion e-commerce with advanced filters',
    icon: '👗',
    link: 'https://guitargroove.onrender.com/',
    gradient: 'from-rose-500 to-red-500'
  },
  {
    id: 6,
    title: 'Agency Website',
    category: 'portfolio',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop',
    description: 'Creative agency website with stunning animations',
    icon: '✨',
    link: 'https://personalportfolio1.onrender.com/',
    gradient: 'from-indigo-500 to-purple-500'
  }
];

const Portfolio = () => {
  const [items, setItems] = useState(projects);
  const [activeFilter, setActiveFilter] = useState('all');
  const [hoveredId, setHoveredId] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const cards = document.querySelectorAll('.project-card');
    cards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, [items]);

  const filterProjects = (category) => {
    setActiveFilter(category);
    if (category === 'all') {
      setItems(projects);
    } else {
      const filtered = projects.filter((item) => item.category === category);
      setItems(filtered);
    }
  };

  const filters = [
    { id: 'all', label: 'All Projects', icon: '✦' },
    { id: 'ecommerce', label: 'E-Commerce', icon: '🛒' },
    { id: 'crypto', label: 'Crypto', icon: '₿' },
    { id: 'portfolio', label: 'Portfolio', icon: '🎨' }
  ];

  return (
    <section className="portfolio" id="work" ref={sectionRef}>
      <div className="portfolio__container">
        {/* Header */}
        <div className="portfolio__header">
          <span className="portfolio__badge">My Portfolio</span>
          <h2 className="portfolio__title">
            Featured <span className="gradient-text">Projects</span>
          </h2>
          <div className="portfolio__underline"></div>
          <p className="portfolio__subtitle">
            A showcase of my best work and creative solutions
          </p>
        </div>

        {/* Filters */}
        <div className="portfolio__filters">
          {filters.map((filter) => (
            <button
              key={filter.id}
              className={`filter-btn ${activeFilter === filter.id ? 'active' : ''}`}
              onClick={() => filterProjects(filter.id)}
            >
              <span className="filter-icon">{filter.icon}</span>
              {filter.label}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="portfolio__grid">
          {items.map((project, index) => (
            <div
              key={project.id}
              className="project-card"
              style={{ transitionDelay: `${index * 0.1}s` }}
              onMouseEnter={() => setHoveredId(project.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <a 
                href={project.link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="project-link"
              >
                <div className="project-image-wrapper">
                  <img 
                    src={project.image} 
                    alt={project.title} 
                    className="project-image"
                    loading="lazy"
                  />
                  
                  {/* Gradient Overlay */}
                  <div className="project-overlay">
                    <div className="project-overlay-content">
                      <span className="project-icon">{project.icon}</span>
                      <h3 className="project-title">{project.title}</h3>
                      <p className="project-description">{project.description}</p>
                      <span className="project-category">{project.category}</span>
                      <div className="project-arrow">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card Footer */}
                <div className="project-footer">
                  <div>
                    <span className="project-category-tag">{project.category}</span>
                    <h4 className="project-name">{project.title}</h4>
                  </div>
                  <div className="project-link-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M18 13V19C18 19.5304 17.7893 20.0391 17.4142 20.4142C17.0391 20.7893 16.5304 21 16 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V8C3 7.46957 3.21071 6.96086 3.58579 6.58579C3.96086 6.21071 4.46957 6 5 6H11M15 3H21M21 3V9M21 3L10 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              </a>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="portfolio__cta">
          <p>Have a project in mind?</p>
          <a href="#contact" className="cta-btn">
            Let's Work Together
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Portfolio;